import React, { useState, useEffect } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate, useParams } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import { getCurrentUser } from "../../utils/userStorage";

const UpdateServiceArea = () => {
  const { id } = useParams();
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const navigate = useNavigate();

  // Fallback to get CompID from storage if Redux fails
  const getCompID = () => {
    if (CompID) return CompID;
    
    // Try to get from storage
    const storedUser = getCurrentUser();
    if (storedUser) {
      return storedUser?.userData?.compID;
    }
    
    return null;
  };

  const effectiveCompID = getCompID();

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    capacity: "",
    isActive: true,
  });
  const [serviceTimes, setServiceTimes] = useState([]);
  const [serviceAreaTables, setServiceAreaTables] = useState([]);
  const [serviceAreaAndTimeRelations, setServiceAreaAndTimeRelations] = useState([]);

  useEffect(() => {
    const fetchServiceArea = async () => {
      if (!id) {
        showErrorToast("Invalid service area ID");
        navigate("/diningAreas");
        return;
      }

      if (!effectiveCompID) {
        showErrorToast("Company ID is missing. Please log in again.");
        navigate("/diningAreas");
        return;
      }

      setInitialLoading(true);
      try {
        const response = await axiosInstance.get(
          `${APIs.GET_SERVICE_AREA_BY_ID}?ID=${id}&CompID=${effectiveCompID}`
        );

        if (response.data.status && response.data.data) {
          const serviceAreaData = response.data.data;
          
          // Set basic form data
          setFormData({
            name: serviceAreaData.name || "",
            description: serviceAreaData.description || "",
            capacity: serviceAreaData.capacity || "",
            isActive: serviceAreaData.isActive !== false,
          });

          // Set related data
          setServiceTimes(serviceAreaData.ServiceTimes || []);
          setServiceAreaTables(serviceAreaData.ServiceAreaTables || []);
          setServiceAreaAndTimeRelations(serviceAreaData.ServiceAreaAndTimeRelations || []);
        } else {
          showErrorToast(
            response.data.message || "Failed to fetch service area"
          );
          navigate("/diningAreas");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to fetch service area. Please try again.";
        showErrorToast(errorMessage);
        navigate("/diningAreas");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchServiceArea();
  }, [id, effectiveCompID, navigate]);

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      showErrorToast("Service area name is required");
      return false;
    }

    if (!effectiveCompID) {
      showErrorToast("Company ID is missing");
      return false;
    }

    if (formData.capacity && (isNaN(formData.capacity) || parseInt(formData.capacity) < 0)) {
      showErrorToast("Capacity must be a valid positive number");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      ID: parseInt(id),
      CompID: effectiveCompID,
      Name: formData.name,
      Description: formData.description,
      Capacity: formData.capacity ? parseInt(formData.capacity) : 0,
      IsActive: formData.isActive,
      ServiceAreaAndTimeRelations: serviceAreaAndTimeRelations,
      ServiceTimes: serviceTimes,
      ServiceAreaTables: serviceAreaTables,
    };

    try {
      setLoading(true);
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_SERVICE_AREA,
        payload
      );

      if (response.data.status) {
        showSuccessToast("Service area updated successfully");
        navigate("/diningAreas");
      } else {
        showErrorToast(
          response.data.message || "Failed to update service area"
        );
      }
    } catch (error) {
      let errorMessage = "An error occurred while updating service area";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        if (Array.isArray(error.response.data.errors)) {
          errorMessage = error.response.data.errors.join(", ");
        } else {
          errorMessage = Object.values(error.response.data.errors)
            .flat()
            .join(", ");
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {(loading || initialLoading) && <Preloader />}
      <div className="d-flex align-items-center mb-4">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/diningAreas">Dining Areas</Link>
            </li>
            <li className="breadcrumb-item active">Update Service Area</li>
          </ol>
          <h1 className="page-header mb-0">
            <i className="fa fa-building me-2" style={{ color: themeColor }}></i>
            Update Service Area
          </h1>
          <p className={isDarkMode ? 'text-light' : 'text-muted'} style={{ opacity: 0.8 }}>
            Modify the service area information and settings
          </p>
        </div>
        <div className="ms-auto">
          <Link to="/diningAreas">
            <button
              className="btn btn-outline-secondary px-4"
              style={{
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
              }}
            >
              <i className="fa fa-arrow-left me-2"></i>
              Back to List
            </button>
          </Link>
        </div>
      </div>

      <div className="card border-0 shadow-sm" style={{ backgroundColor: isDarkMode ? '#1f2937' : '#ffffff' }}>
        <div className="card-header border-0 pb-0" style={{ backgroundColor: isDarkMode ? '#1f2937' : '#ffffff' }}>
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0">
              <div
                className="rounded-circle p-3 me-3"
                style={{
                  backgroundColor: `${themeColor}20`,
                  border: `2px solid ${themeColor}40`
                }}
              >
                <i className="fa fa-building fs-4" style={{ color: themeColor }}></i>
              </div>
            </div>
            <div className="flex-grow-1">
              <h4 className="mb-1" style={{ color: isDarkMode ? '#f9fafb' : '#1f2937' }}>Service Area Details</h4>
              <p className="mb-0" style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                Update the basic information for this service area
              </p>
            </div>
          </div>
        </div>
        <div className="card-body p-4">
          <div className="card mb-4 border-0 shadow-sm" style={{ backgroundColor: isDarkMode ? '#374151' : '#ffffff' }}>
            <div className="card-header border-0" style={{ backgroundColor: isDarkMode ? '#374151' : '#f8f9fa' }}>
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle p-2 me-3"
                  style={{
                    backgroundColor: `${themeColor}20`,
                    border: `1px solid ${themeColor}40`
                  }}
                >
                  <i className="fa fa-info-circle" style={{ color: themeColor }}></i>
                </div>
                <div>
                  <h6 className="mb-0" style={{ color: isDarkMode ? '#f9fafb' : '#1f2937' }}>Basic Information</h6>
                  <small style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                    Enter the name, description, and capacity for this service area
                  </small>
                </div>
              </div>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <FormInput
                    label="Service Area Name *"
                    placeHolder="Enter service area name"
                    type="text"
                    value={formData.name}
                    onchange={handleInputChange("name")}
                  />
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="Capacity"
                    placeHolder="Enter capacity (optional)"
                    type="number"
                    value={formData.capacity}
                    onchange={handleInputChange("capacity")}
                  />
                </div>
                <div className="col-12">
                  <FormInput
                    label="Description"
                    placeHolder="Enter description (optional)"
                    type="text"
                    value={formData.description}
                    onchange={handleInputChange("description")}
                  />
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange("isActive")}
                      style={{
                        accentColor: themeColor,
                      }}
                    />
                    <label className="form-check-label" htmlFor="isActive" style={{ color: isDarkMode ? '#f9fafb' : '#1f2937' }}>
                      Active Service Area
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Times Section */}
          {serviceTimes.length > 0 && (
            <div className="card mb-4 border-0 shadow-sm" style={{ backgroundColor: isDarkMode ? '#374151' : '#ffffff' }}>
              <div className="card-header border-0" style={{ backgroundColor: isDarkMode ? '#374151' : '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-2 me-3"
                    style={{
                      backgroundColor: `${themeColor}20`,
                      border: `1px solid ${themeColor}40`
                    }}
                  >
                    <i className="fa fa-clock" style={{ color: themeColor }}></i>
                  </div>
                  <div>
                    <h6 className="mb-0" style={{ color: isDarkMode ? '#f9fafb' : '#1f2937' }}>Associated Service Times</h6>
                    <small style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                      Service times linked to this area
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  {serviceTimes.map((serviceTime, index) => (
                    <div key={index} className="col-md-6">
                      <div className="p-3 rounded" style={{ backgroundColor: isDarkMode ? '#1f2937' : '#f8f9fa' }}>
                        <h6 className="mb-1" style={{ color: isDarkMode ? '#f9fafb' : '#1f2937' }}>
                          {serviceTime.sT_Name || 'Unnamed Service Time'}
                        </h6>
                        <p className="mb-0 small" style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                          {serviceTime.sT_Description || 'No description'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Service Area Tables Section */}
          {serviceAreaTables.length > 0 && (
            <div className="card mb-4 border-0 shadow-sm" style={{ backgroundColor: isDarkMode ? '#374151' : '#ffffff' }}>
              <div className="card-header border-0" style={{ backgroundColor: isDarkMode ? '#374151' : '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-2 me-3"
                    style={{
                      backgroundColor: `${themeColor}20`,
                      border: `1px solid ${themeColor}40`
                    }}
                  >
                    <i className="fa fa-table" style={{ color: themeColor }}></i>
                  </div>
                  <div>
                    <h6 className="mb-0" style={{ color: isDarkMode ? '#f9fafb' : '#1f2937' }}>Associated Tables</h6>
                    <small style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                      Tables linked to this service area
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  {serviceAreaTables.map((table, index) => (
                    <div key={index} className="col-md-4">
                      <div className="p-3 rounded" style={{ backgroundColor: isDarkMode ? '#1f2937' : '#f8f9fa' }}>
                        <h6 className="mb-1" style={{ color: isDarkMode ? '#f9fafb' : '#1f2937' }}>
                          Table {table.tableNumber || table.id}
                        </h6>
                        <p className="mb-0 small" style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                          Capacity: {table.capacity || 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-3 mt-4">
            <Link to="/diningAreas">
              <button
                className="btn btn-outline-secondary px-4"
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                <i className="fa fa-times me-2"></i>
                Cancel
              </button>
            </Link>
            <button
              className="btn px-4"
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onClick={handleSubmit}
              disabled={loading}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fa fa-save me-2"></i>
                  Update Service Area
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateServiceArea;
